import { trigger } from '@angular/animations';
import { AsyncPipe, DatePipe, Location, LowerCasePipe, UpperCasePipe, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AssetDto, AssetVersionDto, GalleryDto } from '@picflow/types';
import { Subject, takeUntil } from 'rxjs';
import { fadeDownIf } from '../../../animations/fade-down.if.animation';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { moveDownIf } from '../../../animations/move-down.if.animation';
import { Rect } from '../../../classes/rect.class';
import { getBaseZoom } from '../../../consts';
import { AssetDownloadButtonComponent } from '../../../gallery/components/asset-download-button/asset-download-button.component';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { debounce } from '../../../lib/debounce';
import { devicePixelRatio } from '../../../lib/device-pixel-ratio';
import { getAllRouteData } from '../../../lib/get-all-route-data';
import { isMetaKey } from '../../../lib/is-meta-key';
import { sleep } from '../../../lib/sleep';
import { AnalyticsService } from '../../../services/analytics.service';
import { AssetDisplayService } from '../../../services/asset-display.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { ToastService } from '../../../services/toast.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { ReplacePipe } from '../../../shared/pipes/replace.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { AssetVersionCompareMenuComponent } from '../../components/asset-version-compare-menu/asset-version-compare-menu.component';
import { AssetVersionMenuComponent } from '../../components/asset-version-menu/asset-version-menu.component';
import { ImageViewerControlsComponent } from '../../components/image-viewer-controls/image-viewer-controls.component';
import { ImageViewerPreviewComponent } from '../../components/image-viewer-preview/image-viewer-preview.component';
import { ImageViewerComponent } from '../../components/image-viewer/image-viewer.component';
import { VideoTimeData, VideoViewerComponent } from '../../components/video-viewer/video-viewer.component';
import { DeleteAssetVersionDialogComponent } from '../../dialogs/delete-asset-version-dialog/delete-asset-version-dialog.component';
import { CanDisplayAssetPipe } from '../../pipes/can-display-asset.pipe';

@Component({
  selector: 'app-asset-version-page',
  templateUrl: './asset-version-page.component.html',
  styleUrls: ['./asset-version-page.component.scss'],
  animations: [
    trigger('assetControls', moveDownIf),
    trigger('header', moveDownIf),
    trigger('actionContainer', fadeUpIf),
    trigger('versionName', fadeDownIf),
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeaderComponent,
    TooltipDirective,
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    ResizeDirective,
    ImageViewerPreviewComponent,
    ImageViewerControlsComponent,
    ImageViewerComponent,
    RouterLink,
    AssetVersionMenuComponent,
    AssetVersionCompareMenuComponent,
    UpperCasePipe,
    DatePipe,
    ReplacePipe,
    TranslatePipe,
    VideoViewerComponent,
    AssetVersionUrlPipe,
    AsyncPipe,
    CanDisplayAssetPipe,
    AssetDownloadButtonComponent,
    LowerCasePipe,
  ],
})
export class AssetVersionPageComponent implements OnInit, OnDestroy {
  private check = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private assetDisplayService = inject(AssetDisplayService);
  private assetService = inject(AssetService);
  private dialog = inject(DialogService);
  private router = inject(Router);
  private toasts = inject(ToastService);
  private location = inject(Location);
  private analytics = inject(AnalyticsService);
  private platformId = inject<string>(PLATFORM_ID);
  @ViewChild('assetViewerSection', { static: false }) assetViewerSection: ElementRef;
  asset: AssetDto = getAllRouteData(this.route.snapshot).data.asset;
  gallery: GalleryDto = getAllRouteData(this.route.snapshot).gallery;
  version: AssetVersionDto = getAllRouteData(this.route.snapshot).data.version;
  versions: AssetVersionDto[] = getAllRouteData(this.route.snapshot).data.versions;
  scale: number;
  offsetScale: number;
  offset: Rect = new Rect();
  dragging: boolean;
  zooming: boolean;
  fitButton: 'width' | 'height';
  currentNumberOfTouches = 0;
  previousTouches: Rect[];
  previousTouchStartTime = 0;
  lastTouchEndTime = 0;
  touchStartTime = 0;
  showControls = true;
  container: Rect;
  previousVersion: AssetVersionDto;
  previousCompareVersion: AssetVersionDto;
  nextVersion: AssetVersionDto;
  nextCompareVersion: AssetVersionDto;
  comparisonPosition = 50;
  comparisonVisualPosition = 50;
  comparisonMode = false;
  compareVersion: AssetVersionDto;
  mousePos = new Rect();
  crop: Rect = new Rect();
  accessLevel: GalleryAccessLevel = getAllRouteData(this.route.snapshot).accessLevel;
  disableOverlayTransitions = false;
  videoTime: VideoTimeData;
  mouseHasMoved = false;
  canZoom: boolean;
  destroyed$ = new Subject<void>();

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.key === 'Escape') {
      if (this.comparisonMode) {
        this.toggleComparisonMode();
        this.analytics.reportKeyboardShortcut('exit-comparison-mode', 'asset-version-page');
      } else {
        this.analytics.reportKeyboardShortcut('exit-asset-version-page', 'asset-version-page');
        this.close();
      }
    }
    if (event.key === 'ArrowLeft' && this.previousVersion) {
      this.analytics.reportKeyboardShortcut('previous-version', 'asset-version-page');
      this.router.navigate(['../', this.previousVersion.id], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      });
    }
    if (event.key === 'ArrowRight' && this.nextVersion) {
      this.analytics.reportKeyboardShortcut('next-version', 'asset-version-page');
      this.router.navigate(['../', this.nextVersion.id], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      });
    }

    if (event.key === 'c') {
      if (this.asset.type === 'video') return;
      this.analytics.reportKeyboardShortcut('toggle-comparison-mode', 'asset-version-page');
      this.toggleComparisonMode();
    }

    if ((event.key === '=' || event.key === '+') && metaKey) {
      if (this.asset.type === 'video') return;
      this.analytics.reportKeyboardShortcut('zoom-in', 'asset-version-page');
      event.preventDefault();
      this.onScale('in');
    }
    if (event.key === '-' && metaKey) {
      if (this.asset.type === 'video') return;
      this.analytics.reportKeyboardShortcut('zoom-out', 'asset-version-page');
      event.preventDefault();
      this.onScale('out');
    }

    if (event.key === '0' && metaKey) {
      if (this.asset.type === 'video') return;
      this.analytics.reportKeyboardShortcut('zoom-to-full', 'asset-version-page');
      this.onScale('full');
    }
  }

  @HostListener('window:resize', ['$event']) async winResize() {
    this.calcAssetSizes();
    this.container = this.getContainerSize();
    this.check.detectChanges();
  }

  constructor() {
    this.container = this.getContainerSize();
  }

  async ngOnInit() {
    this.calcPreviousAndNextVersions();

    // without this double, we don't have a correct size on page load
    this.scale = this.assetDisplayService.getBaseZoom(this.offsetScale, this.scale);
    this.calcAssetSizes();
    this.scale = this.assetDisplayService.getBaseZoom(this.offsetScale, this.scale);
    this.calcAssetSizes();
    this.check.detectChanges();

    this.route.data.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      const data = getAllRouteData(this.route.snapshot);
      this.gallery = data.gallery;
      this.asset = data.data.asset;
      this.version = data.data.version;
      this.versions = data.data.versions;

      this.calcPreviousAndNextVersions();

      // without this double, we don't have a correct size on page load
      this.scale = this.assetDisplayService.getBaseZoom(this.offsetScale, this.scale);
      this.calcAssetSizes();

      if (this.comparisonMode) this.calcOnAssetPosition();
      this.check.detectChanges();
    });

    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.setCompareVersion();
      this.calcPreviousAndNextVersions();
    });

    if (isPlatformServer(this.platformId)) return;
    this.container = this.getContainerSize();
    this.mousePos = new Rect(window.innerWidth / 2, window.innerHeight / 2);
    await sleep(0);
    if (!this.assetViewerSection) await sleep(100);
    if (!this.assetViewerSection) return;
    this.assetViewerSection.nativeElement.addEventListener('wheel', this.onWheel.bind(this));
  }

  private setCompareVersion() {
    this.compareVersion = this.asset.version_data;
    const query = this.route.snapshot.queryParams;
    if (query.compareTo) {
      this.comparisonMode = true;
      const version = this.versions.find(v => v.id === query.compareTo);
      if (version) this.compareVersion = version;
    }
    this.check.detectChanges();
  }

  private calcPreviousAndNextVersions() {
    const versions = this.comparisonMode ? this.versions.filter(v => v.id !== this.compareVersion?.id) : this.versions;
    versions.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
    const index = versions.findIndex(v => v.id === this.version.id);
    this.previousVersion = versions[index - 1];
    this.nextVersion = versions[index + 1];

    if (this.compareVersion) {
      const compareVersions = this.versions.filter(v => v.id !== this.version?.id);
      const pIndex = compareVersions.findIndex(v => v.id === this.compareVersion.id);
      this.previousCompareVersion = compareVersions[pIndex - 1];
      this.nextCompareVersion = compareVersions[pIndex + 1];
    }
    this.check.detectChanges();
  }

  public getScalePercent() {
    return Math.round(this.offsetScale * 100 * this.scale * devicePixelRatio());
  }

  public onWheel(event: WheelEvent) {
    if (this.asset.type === 'video') return;
    this.zooming = true;
    event.preventDefault();

    const container = new Rect(0, 0, window.innerWidth, window.innerHeight);
    const { scale, offsetChange } = this.assetDisplayService.onWheel(event, container, this.scale, this.offsetScale);

    this.scale = scale;
    this.offset = this.offset.add(offsetChange);

    this.onScale(undefined);
    this.stopZooming();
    return false;
  }

  private stopZooming = debounce(() => {
    this.zooming = false;
    this.calcAssetSizes();
  }, 60);

  onScale(dir: string) {
    const container = new Rect(0, 0, window.innerWidth, window.innerHeight);
    this.scale =
      this.assetDisplayService.dirToScale(this.version, dir, container, this.scale, this.offsetScale) || this.scale;

    if (dir === 'height' || dir === 'width') {
      this.offset.x = 0;
      this.offset.y = 0;
    }

    this.calcAssetSizes();
  }

  getBaseZoom() {
    return this.assetDisplayService.getBaseZoom(this.offsetScale, getBaseZoom());
  }

  private async calcComparisonLinePosition(pos: Rect) {
    await sleep(0);
    if (this.comparisonMode) {
      this.comparisonPosition = (pos.x / window.innerWidth) * 100;
      this.comparisonVisualPosition = Math.max(0, Math.min(100, ((pos.x - this.crop.x) / this.crop.width) * 100));
      this.check.detectChanges();
    }
  }

  mouseDown(event: MouseEvent) {
    if (event.buttons !== 1 && event.buttons !== 4) return;

    this.previousTouchStartTime = this.touchStartTime;
    this.touchStartTime = Date.now();

    event.preventDefault(); // needed for firefox dragging to work
    this.dragging = true;

    this.calcComparisonLinePosition(Rect.fromMouseEvent(event));
    this.mouseHasMoved = false;
    this.check.detectChanges();
  }

  mouseMove(event: MouseEvent) {
    this.mousePos = Rect.fromMouseEvent(event);
    if (event.buttons === 1 || event.buttons === 4) {
      if (event.buttons === 4 && !this.dragging) {
        this.dragging = true;
        this.check.detectChanges();
      }

      if (this.asset.type === 'video') return;
      this.offset.x += (event.movementX * 1) / this.scale; // / devicePixelRatio();
      this.offset.y += (event.movementY * 1) / this.scale; // / devicePixelRatio();
      this.calcAssetSizes();
    } else {
      if (this.dragging) this.mouseUp(event);
    }
    this.calcComparisonLinePosition(Rect.fromMouseEvent(event));
    this.mouseHasMoved = true;
    this.check.detectChanges();
  }

  mouseUp(event: MouseEvent) {
    const path = event.composedPath();
    if (path.some((el: HTMLElement) => el.tagName === 'APP-HEADER')) return;

    this.dragging = false;
    this.calcAssetSizes();
    if (!this.mouseHasMoved) {
      const scalePercent = this.getScalePercent();
      if (scalePercent < 100 || scalePercent > 100) {
        if (this.scale <= 1) {
          const container = this.getContainerSize();

          const offsetX = (event.clientX - container.width / 2) / this.scale;
          const offsetY = (event.clientY - container.height / 2) / this.scale;

          this.offset.x += -offsetX;
          this.offset.y += -offsetY;
        }

        this.onScale('full');
      } else this.onScale('fit');
      this.lastTouchEndTime = 0;
    }

    this.calcAssetSizes();
  }

  touchStart(event: TouchEvent) {
    this.mousePos = Rect.fromTouchEvent(event)[0];
    this.currentNumberOfTouches += event.changedTouches.length;
    this.previousTouches = Rect.fromTouchEvent(event);
    this.check.detectChanges();
  }

  touchMove(event: TouchEvent) {
    this.dragging = true;

    if (event.touches.length === 1) this.touchMoveSingle(event);
    else this.touchMoveMultiple(event);

    this.previousTouches = Rect.fromTouchEvent(event);
    this.check.detectChanges();
  }

  touchEnd(event: TouchEvent) {
    this.currentNumberOfTouches -= event.changedTouches.length;
    if (this.currentNumberOfTouches === 0) {
      this.dragging = false;
      this.previousTouches = [];
    }
    this.calcAssetSizes();
  }

  touchMoveSingle(event: TouchEvent) {
    this.mousePos = Rect.fromTouchEvent(event)[0];
    const touch = Rect.fromTouch(event.touches[0]);
    const { offsetChange } = this.assetDisplayService.touchMoveSingle(
      this.offsetScale,
      this.scale,
      touch,
      this.previousTouches[0]
    );

    if (this.comparisonMode) {
      this.calcComparisonLinePosition(touch);
    } else {
      this.offset = this.offset.add(offsetChange);
    }
    this.check.detectChanges();
  }

  touchMoveMultiple(event: TouchEvent) {
    const touches = Rect.fromTouchEvent(event);
    if (!touches.length || this.previousTouches.length < 2) return;

    const { offsetChange, scale } = this.assetDisplayService.touchMoveMultiple(
      event,
      this.previousTouches,
      this.scale,
      this.offsetScale
    );

    this.offset = this.offset.add(offsetChange);
    this.scale = scale;

    this.calcAssetSizes();
  }

  getNiceName(type: string) {
    const array = this.asset.version_data.asset_name.split('.');
    array.pop();
    const string = array.join('.');
    if (type === 'first') {
      return string.substring(0, string.length - 8);
    }
    return string.substring(string.length - 8);
  }

  calcAssetSizes() {
    this.container = this.getContainerSize();
    const boundedContainer = this.assetDisplayService.getBoundedContainerSize(this.version, this.container);

    console.log(this.container);

    const { left, right, top, bottom } = this.assetDisplayService.getScaledBounds(
      this.version,
      this.container,
      this.offset,
      this.scale
    );

    if (!this.dragging) {
      if (left < 0) this.offset.x += left * (1 / this.scale);
      else if (right < 0) this.offset.x -= right * (1 / this.scale);

      if (top < 0) this.offset.y += top * (1 / this.scale);
      else if (bottom < 0) this.offset.y -= bottom * (1 / this.scale);
    }

    const _data = this.version.data?.width ? this.version.data : this.getDataBackup();
    this.offsetScale = boundedContainer.width / (_data.width || 1);

    const zoom = this.assetDisplayService.getBaseZoom(this.offsetScale, this.scale);
    this.fitButton = this.assetDisplayService.getButtonFit(this.version, this.container, zoom, this.scale);

    this.canZoom = this.asset.type === 'image' && this.offsetScale < 1 / devicePixelRatio();

    if (this.mousePos) this.calcComparisonLinePosition(this.mousePos);
    this.calcOnAssetPosition();
  }

  onPreviewMove(offset: Rect) {
    this.offset = offset;
    this.check.detectChanges();
  }

  async makePrimary(version: AssetVersionDto) {
    await this.assetService.updateAsset(
      this.asset,
      { version: version.id },
      <string>this.asset.gallery,
      <string>this.asset.section
    );
    this.asset = await this.assetService.getAsset(this.asset.id, <string>this.asset.gallery);

    this.toasts.add('Version marked as Primary', 'success');
    if (this.comparisonMode) this.toggleComparisonMode();
    this.setCompareVersion();
  }

  async deleteVersion(version: AssetVersionDto) {
    const resp = await this.dialog.open(DeleteAssetVersionDialogComponent, {});
    if (!resp) return;
    await this.assetService.deleteAssetVersion(version.id);

    this.asset['versions_total_count'] -= 1;
    if (this.asset['versions_total_count'] === 1) {
      this.assetService.updateAssetVersion(<string>this.asset.version, { version_name: 'v1' });
    }

    await sleep(500); // waiting for db to update versions_total_count
    this.location.back();
    this.toasts.add('Version deleted', 'success');
  }

  async toggleComparisonMode() {
    this.comparisonMode = !this.comparisonMode;
    this.calcPreviousAndNextVersions();
    this.comparisonPosition = 50;
    this.comparisonVisualPosition = 50;
    if (this.comparisonMode && this.mousePos) {
      this.calcComparisonLinePosition(this.mousePos);
    }
    this.disableOverlayTransitions = true;
    this.calcOnAssetPosition();
    await sleep(0);
    this.disableOverlayTransitions = false;
    this.check.detectChanges();
  }

  switchVersion(version: AssetVersionDto) {
    this.router.navigate(['../', version.id], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
      replaceUrl: true,
    });
  }

  switchCompareVersion(version: AssetVersionDto) {
    this.router.navigate(['./'], { relativeTo: this.route, queryParams: { compareTo: version.id }, replaceUrl: true });
  }

  getDataBackup() {
    return { width: 250 * devicePixelRatio(), height: 250 * devicePixelRatio() };
  }

  close() {
    this.location.back();
  }

  private calcOnAssetPosition() {
    const s = this.offsetScale * this.scale;

    const extraVerticalSpace = this.asset?.type === 'video' ? 200 : 0;

    const _data = this.version.data?.width ? this.version.data : this.getDataBackup();
    this.crop = new Rect(
      this.container.width / 2 - (_data.width * s) / 2 + this.offset.x * this.scale,
      this.container.height / 2 - (_data.height * s) / 2 + this.offset.y * this.scale + extraVerticalSpace / 2,
      _data.width * s,
      _data.height * s
    );

    const rX = this.crop.x < 0 ? -this.crop.x : 0;
    const rY = this.crop.y < 0 ? -this.crop.y : 0;

    if (rX > 0) {
      this.crop.width -= rX;
      this.crop.x = 0;
    }
    if (rY > 0) {
      this.crop.height -= rY;
      this.crop.y = 0;
    }
    if (this.crop.width + this.crop.x > this.container.width) {
      this.crop.width = this.container.width - this.crop.x;
    }
    if (this.crop.height + this.crop.y > this.container.height) {
      this.crop.height = this.container.height - this.crop.y;
    }
    this.check.detectChanges();
  }

  getContainerSize(): Rect {
    const extraVerticalSpace = this.asset?.type === 'video' ? 200 : 0;
    return new Rect(0, 0, window.innerWidth, window.innerHeight - extraVerticalSpace);
  }

  get allowDownload() {
    if (this.accessLevel === 'edit') return true;
    if (!this.gallery.download_single_asset) return false;
    if (this.gallery.approved_downloads_only) {
      return this.asset.status === 'approved';
    }
    return true;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
